<script setup lang="ts">
/**
 * Обложка сайта, подключённая к настройкам заведения.
 *
 * Обложка — настройка САЙТА: в приложении из сторов её нет (там первый экран
 * занимает нативный сплеш и интро-видео). Поэтому в нативной сборке компонент
 * не рисует ничего.
 */
import { computed } from 'vue';
import SiteCoverView from './SiteCoverView.vue';
import { useAppearance, useInstitutionStore } from '@entities/institution';
import { coverStretchesFullBleed } from '@shared/config';

const institutionStore = useInstitutionStore();
const { appearance, surface } = useAppearance();

const cover = computed(() => appearance.value.cover);

const visual = computed(() => (institutionStore.institution as any)?.visual ?? {});

const isVisible = computed(() => surface.value === 'site' && cover.value.layout !== 'none');

/**
 * Обложка от края до края: не просто во всю ширину, но и от самого верха страницы.
 *
 * Под фиксированную шапку `main` резервирует отступ сверху — из-за него над баннером
 * оставалась полоса фона высотой в шапку. Вычитаем этот отступ обратно: величину
 * лейаут кладёт в `--app-main-pt` (layouts/default.vue), в CSS её иначе не достать.
 * Шапка при этом становится прозрачной и ложится на баннер — см. widgets/hd.
 */
const isBleed = computed(() => coverStretchesFullBleed(cover.value));

/**
 * Прозрачная шапка лежит НА обложке — в этом её смысл, поэтому там отступ сверху
 * не нужен. При обычной шапке обложка иначе упирается в неё вплотную.
 */
const isFlush = computed(() => appearance.value.header.surface === 'transparent');
</script>

<template>
  <SiteCoverView
    v-if="isVisible"
    class="site-cover-slot"
    :class="{ 'site-cover-slot--flush': isFlush, 'site-cover-slot--bleed': isBleed }"
    :cover="cover"
    :image-url="visual.banner_img"
    :logo-url="visual.logo_file"
    :fallback-title="institutionStore.institution?.name || ''"
  />
</template>

<style scoped>
/*
  --app-cover-top — сколько экрана остаётся НАД обложкой: отступ под фиксированную
  шапку (`--app-main-pt` из layouts/default.vue) плюс собственный отступ слота.
  Из него высота «во весь экран» вычитает свой размер (COVER_HEIGHT_CSS), поэтому
  переменная обязана повторять margin-top один в один — иначе низ обложки
  промахнётся мимо нижней кромки экрана ровно на разницу.
*/
.site-cover-slot {
  /* Сверху — воздух до шапки, иначе обложка упирается в неё вплотную.
     Снизу — отступ как у первого блока контента. */
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  --app-cover-top: calc(var(--app-main-pt, 0px) + 1.5rem);
}

@media (min-width: 768px) {
  .site-cover-slot {
    margin-top: 2rem;
    --app-cover-top: calc(var(--app-main-pt, 0px) + 2rem);
  }
}

/* Прозрачная шапка лежит на обложке — отступ там всё бы испортил */
.site-cover-slot--flush {
  margin-top: 0;
  --app-cover-top: var(--app-main-pt, 0px);
}

/* От края до края: съедаем отступ, зарезервированный под фиксированную шапку
   (она `position: fixed`, отступ держит `main`), и уходим под неё.
   Правило стоит последним — специфичность у всех правил слота одинаковая,
   и на широких экранах оно перебивает `margin-top: 2rem` из медиазапроса выше. */
.site-cover-slot--bleed {
  margin-top: calc(-1 * var(--app-main-pt, 0px));
  --app-cover-top: 0px;
}
</style>
